import Signer from '@aws-amplify/core/lib-esm/Signer';
import {Credentials} from 'aws-sdk';
import {ChatTransport, UIMessageChunk} from 'ai';
import type {UIMessage} from '@ai-sdk/react';

let cachedBaseUrl = '';

async function getBaseUrl(): Promise<string> {
    if (cachedBaseUrl) return cachedBaseUrl;
    const res = await fetch(`${window.location.origin}/settings.json`);
    const settings = await res.json();
    cachedBaseUrl = settings.NA?.cassandraEndpoint ?? '';
    return cachedBaseUrl;
}

let userId = 'portal-user';

export function setUserId(id: string) {
    userId = id;
}

function signedFetch(url: string, body: string, credentials: Credentials, signal?: AbortSignal): Promise<Response> {
    const signed = Signer.sign(
        {url, method: 'POST', headers: {'Content-Type': 'application/json'}, body, data: body},
        {
            access_key: credentials.accessKeyId,
            secret_key: credentials.secretAccessKey,
            session_token: credentials.sessionToken,
        },
        {region: 'us-east-1', service: 'lambda'}
    );
    return fetch(url, {
        method: 'POST',
        headers: {...signed.headers, 'Amz-Mons-Idp-Subject': userId},
        body,
        signal,
    });
}

/**
 * Parses an oRPC SSE stream into a ReadableStream<UIMessageChunk> that the AI SDK's useChat understands.
 *
 * The backend sends oRPC-wrapped SSE: `data: {"json": <UIMessageChunk>}`
 * The AI SDK expects raw UIMessageChunk objects from the ReadableStream.
 */
function parseORPCStream(response: Response): ReadableStream<UIMessageChunk> {
    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    return new ReadableStream<UIMessageChunk>({
        async start(controller) {
            try {
                let done = false;
                while (!done) {
                    const result = await reader.read();
                    done = result.done;
                    if (result.value) {
                        buffer += decoder.decode(result.value, {stream: true});
                        const lines = buffer.split('\n');
                        buffer = lines.pop() ?? '';
                        for (const line of lines) {
                            if (!line.startsWith('data: ')) continue;
                            try {
                                const parsed = JSON.parse(line.slice(6));
                                const chunk = parsed.json ?? parsed;
                                console.log('[cassandra] chunk:', chunk);
                                controller.enqueue(chunk as UIMessageChunk);
                            } catch {
                                /* skip malformed lines */
                            }
                        }
                    }
                }
                controller.close();
            } catch (err) {
                controller.error(err);
            }
        },
        cancel() {
            reader.cancel();
        },
    });
}

export interface Chat {
    id: string;
    title: string;
    createdAt: string;
    updatedAt: string;
}

export async function listChats(credentials: Credentials): Promise<Chat[]> {
    const baseUrl = await getBaseUrl();
    const res = await signedFetch(`${baseUrl}/chat/list`, JSON.stringify({json: {}}), credentials);
    const data = await res.json();
    const chats = data.json?.chats ?? data.json?.sessions ?? data.json?.items ?? [];
    return chats.map((c: any) => ({...c, id: c.chatId ?? c.id}));
}

export async function createChat(title: string, credentials: Credentials): Promise<Chat> {
    const baseUrl = await getBaseUrl();
    const res = await signedFetch(`${baseUrl}/chat/create`, JSON.stringify({json: {title}}), credentials);
    const data = await res.json();
    const chat = data.json;
    return {...chat, id: chat.chatId ?? chat.id};
}

export async function deleteChat(chatId: string, credentials: Credentials): Promise<void> {
    const baseUrl = await getBaseUrl();
    await signedFetch(`${baseUrl}/chat/delete`, JSON.stringify({json: {chatId}}), credentials);
}

export async function getMessages(chatId: string, credentials: Credentials): Promise<UIMessage[]> {
    const baseUrl = await getBaseUrl();
    const res = await signedFetch(`${baseUrl}/chat/getMessages`, JSON.stringify({json: {chatId}}), credentials);
    const data = await res.json();
    return data.json ?? [];
}

/**
 * ChatTransport for the AI SDK's useChat hook.
 * Signs requests with SigV4 and talks to the Cassandra Lambda function URL via oRPC.
 */
export class CassandraChatTransport implements ChatTransport<UIMessage> {
    private chatIdOverride: string | null = null;

    constructor(private credentials: Credentials) {}

    setChatId(chatId: string) {
        this.chatIdOverride = chatId;
    }

    async sendMessages(options: {
        chatId: string;
        messages: UIMessage[];
        abortSignal?: AbortSignal;
    }): Promise<ReadableStream<UIMessageChunk>> {
        const baseUrl = await getBaseUrl();
        const url = `${baseUrl}/chat/sendMessage`;
        const chatId = this.chatIdOverride ?? options.chatId;

        // Send only the last user message for normal flow (backend has history in S3).
        // Send the last assistant message for resume flows (approval/question responses).
        const lastMessage = options.messages[options.messages.length - 1];
        const isResumeFlow = lastMessage?.role === 'assistant';
        const messagesToSend = isResumeFlow ? [lastMessage] : options.messages.filter((m) => m.role === 'user').slice(-1);

        const body = JSON.stringify({
            json: {
                chatId,
                messages: messagesToSend,
            },
        });

        const res = await signedFetch(url, body, this.credentials, options.abortSignal);

        if (!res.ok) {
            throw new Error(`sendMessage failed: ${res.status} ${res.statusText}`);
        }

        return parseORPCStream(res);
    }

    async reconnectToStream(options: {chatId: string}): Promise<ReadableStream<UIMessageChunk> | null> {
        try {
            const baseUrl = await getBaseUrl();
            const url = `${baseUrl}/chat/resumeStream`;
            const body = JSON.stringify({json: {chatId: options.chatId}});
            const res = await signedFetch(url, body, this.credentials);

            if (!res.ok || !res.body) return null;

            return parseORPCStream(res);
        } catch {
            return null;
        }
    }
}
